import { useMemo, useState } from 'react'
import { Search, Store } from 'lucide-react'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'tailwind-v4/components/input-group'
import { useVenues } from '@/hooks/useVenue'

function VenueInitial({ name }: { name: string }) {
  const initial = (name?.[0] ?? '?').toUpperCase()
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-accent text-[10px] font-semibold text-accent-foreground"
    >
      {initial}
    </span>
  )
}

/**
 * Generic "pick a venue to continue" gate. Used by any org-level page that
 * can only exist under a venue (day planner, floorplan, tables, …). Mirrors
 * the pattern on `/orgId/square/table-mappings` so the UX is consistent
 * across venue-only surfaces.
 */
export function VenuePickerGate({
  label,
  onPick,
  venues: venuesProp,
}: {
  label: string
  onPick: (serial: string) => void
  /**
   * Optional pre-scoped venue list. Omitted → all of the org's venues (the
   * default). Pass a filtered list to narrow the choices — e.g. Square table
   * mappings only offers venues mapped to a Square location.
   */
  venues?: any[]
}) {
  const [search, setSearch] = useState('')
  const [allVenues] = useVenues()
  const venues = venuesProp ?? allVenues

  const filtered = useMemo(() => {
    if (!search) return venues
    const q = search.toLowerCase()
    return venues.filter((v: any) => {
      const name = (v?.alias ?? v?.name ?? '').toLowerCase()
      return name.includes(q)
    })
  }, [venues, search])

  return (
    <div className="flex flex-1 items-start justify-center px-4 py-16">
      <div className="flex w-full max-w-md flex-col gap-6 text-center">
        <div className="mx-auto flex size-10 items-center justify-center rounded-xl border border-border text-muted-foreground">
          <Store className="size-5" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-foreground">
            Continue to {label}
          </h2>
          <p className="text-sm text-muted-foreground">
            Choose a venue to continue
          </p>
        </div>
        <div className="flex flex-col gap-2 text-left">
          <InputGroup className="h-10">
            <InputGroupAddon>
              <Search className="size-4" />
            </InputGroupAddon>
            <InputGroupInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
              }}
              placeholder="Find venue…"
              aria-label="Find venue"
            />
          </InputGroup>
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No venue found.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {filtered.map((venue: any) => {
                const vSerial = venue?.serial
                const vLabel = venue?.alias ?? venue?.name ?? vSerial ?? 'Venue'
                return (
                  <li key={vSerial}>
                    <button
                      type="button"
                      onClick={() => {
                        onPick(vSerial)
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-foreground outline-none transition-colors hover:bg-accent focus-visible:bg-accent"
                    >
                      <VenueInitial name={vLabel} />
                      <span className="truncate">{vLabel}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
